import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText,
  Globe,
  Database,
  Upload,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Clock,
  Trash2,
  Plus,
  X,
  BookOpen,
} from 'lucide-react';
import { apiRequest, API_BASE_URL } from '../../config/api';
import { useToast } from '../../context/ToastContext';

const SOURCE_TYPE_CONFIG = {
  document: {
    icon: FileText,
    label: 'Document',
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
  },
  url: {
    icon: Globe,
    label: 'URL',
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
  },
  crm_object: {
    icon: Database,
    label: 'CRM',
    color: 'text-violet-600 dark:text-violet-400',
    bg: 'bg-violet-50 dark:bg-violet-900/20',
  },
};

const STATUS_CONFIG = {
  pending: {
    icon: Clock,
    label: 'Pending',
    color: 'text-gray-500',
    bg: 'bg-gray-100 dark:bg-gray-800',
  },
  processing: {
    icon: Loader2,
    label: 'Processing',
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    animate: true,
  },
  ready: {
    icon: CheckCircle2,
    label: 'Ready',
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
  },
  failed: {
    icon: AlertCircle,
    label: 'Failed',
    color: 'text-red-500 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-900/20',
  },
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
      <Icon className={`w-3 h-3 ${config.animate ? 'animate-spin' : ''}`} />
      {config.label}
    </span>
  );
}

function SourceTypeIcon({ type }) {
  const config = SOURCE_TYPE_CONFIG[type] || SOURCE_TYPE_CONFIG.document;
  const Icon = config.icon;
  return (
    <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
      <Icon className={`w-4 h-4 ${config.color}`} />
    </div>
  );
}

function AddSourceModal({ chatbotId, onClose, onSourceAdded }) {
  const { showError, showSuccess } = useToast();
  const [sourceType, setSourceType] = useState(null);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        if (name) formData.append('name', name);

        // For file uploads, we use raw fetch (not apiRequest) because
        // apiRequest sets Content-Type: application/json which breaks FormData.
        // This matches the pattern used in useFileUpload.js.
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_BASE_URL}/chatbots/${chatbotId}/knowledge-sources/upload`,
          {
            method: 'POST',
            headers: {
              ...(token && { Authorization: `Bearer ${token}` }),
            },
            body: formData,
          }
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Upload failed');
        }
      }
      showSuccess('Document uploaded successfully');
      onSourceAdded();
      onClose();
    } catch (error) {
      showError(error.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = async () => {
    if (!url.trim()) return;
    setUploading(true);

    try {
      const response = await apiRequest(
        `/chatbots/${chatbotId}/knowledge-sources/`,
        {
          method: 'POST',
          body: JSON.stringify({
            type: 'url',
            name: name || url,
            url: url,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add URL source');
      }

      showSuccess('URL source added — crawling in progress');
      onSourceAdded();
      onClose();
    } catch (error) {
      showError(error.message || 'Failed to add URL source');
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(Array.from(e.dataTransfer.files));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {sourceType ? `Add ${SOURCE_TYPE_CONFIG[sourceType]?.label || 'Source'}` : 'Add Knowledge Source'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          {/* Step 1: Choose type */}
          {!sourceType && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Choose a source type to add to your chatbot's knowledge base.
              </p>
              {[
                { type: 'document', title: 'Upload Document', desc: 'PDF, TXT, or Markdown files' },
                { type: 'url', title: 'Web Page URL', desc: 'Crawl and index a web page' },
                { type: 'crm_object', title: 'CRM Records', desc: 'Connect your CRM data' },
              ].map((opt) => {
                const config = SOURCE_TYPE_CONFIG[opt.type];
                const Icon = config.icon;
                return (
                  <button
                    key={opt.type}
                    onClick={() => setSourceType(opt.type)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left cursor-pointer"
                  >
                    <div className={`w-9 h-9 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-4.5 h-4.5 ${config.color}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{opt.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Step 2: Document upload */}
          {sourceType === 'document' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Name (optional)
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:text-white transition-colors"
                  placeholder="e.g. Product Documentation"
                />
              </div>
              <div
                className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                  dragActive
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/10'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.txt,.md"
                  className="hidden"
                  onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                />
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
                    <p className="text-sm text-gray-600 dark:text-gray-300">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                      <Upload className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Drop files here or <span className="text-brand-600 dark:text-brand-400">browse</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-1">PDF, TXT, MD — up to 10 MB</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: URL input */}
          {sourceType === 'url' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Name (optional)
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:text-white transition-colors"
                  placeholder="e.g. Help Center"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  URL
                </label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:text-white transition-colors"
                  placeholder="https://example.com/docs"
                  ref={(el) => {
                    if (el) requestAnimationFrame(() => el.focus());
                  }}
                />
              </div>
              <button
                onClick={handleUrlSubmit}
                disabled={!url.trim() || uploading}
                className="w-full px-4 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Crawling...
                  </>
                ) : (
                  <>
                    <Globe className="w-4 h-4" />
                    Crawl URL
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 2: CRM Object selector */}
          {sourceType === 'crm_object' && (
            <CrmObjectSelector chatbotId={chatbotId} onSourceAdded={onSourceAdded} onClose={onClose} />
          )}
        </div>

        {/* Back button if type selected */}
        {sourceType && (
          <div className="px-5 pb-4">
            <button
              onClick={() => setSourceType(null)}
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              &larr; Back to source types
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CrmObjectSelector({ chatbotId, onSourceAdded, onClose }) {
  const { showError, showSuccess } = useToast();
  const [objectDefs, setObjectDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function fetchObjects() {
      try {
        const response = await apiRequest('/crm/object-definitions/');
        if (response.ok) {
          const data = await response.json();
          setObjectDefs(data);
        }
      } catch (error) {
        console.error('Failed to fetch CRM objects:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchObjects();
  }, []);

  const handleSubmit = async () => {
    if (!selectedId) return;
    setSubmitting(true);

    const selectedObj = objectDefs.find((o) => o.id === selectedId);
    try {
      const response = await apiRequest(
        `/chatbots/${chatbotId}/knowledge-sources/`,
        {
          method: 'POST',
          body: JSON.stringify({
            type: 'crm_object',
            name: selectedObj?.name || 'CRM Records',
            crm_object_definition_id: selectedId,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add CRM source');
      }

      showSuccess('CRM source added — syncing records');
      onSourceAdded();
      onClose();
    } catch (error) {
      showError(error.message || 'Failed to add CRM source');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (objectDefs.length === 0) {
    return (
      <div className="text-center py-6">
        <Database className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
        <p className="text-sm text-gray-500 dark:text-gray-400">No CRM objects available.</p>
        <p className="text-xs text-gray-400 mt-1">Create object definitions in the CRM module first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Select a CRM object type to sync as knowledge.
      </p>
      <div className="space-y-1.5 max-h-60 overflow-y-auto">
        {objectDefs.map((obj) => (
          <button
            key={obj.id}
            onClick={() => setSelectedId(obj.id)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors cursor-pointer ${
              selectedId === obj.id
                ? 'border-brand-300 bg-brand-50 dark:border-brand-700 dark:bg-brand-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <Database className={`w-4 h-4 ${selectedId === obj.id ? 'text-brand-600 dark:text-brand-400' : 'text-gray-400'}`} />
            <span className={`text-sm font-medium ${selectedId === obj.id ? 'text-brand-700 dark:text-brand-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {obj.name}
            </span>
          </button>
        ))}
      </div>
      <button
        onClick={handleSubmit}
        disabled={!selectedId || submitting}
        className="w-full px-4 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Adding...
          </>
        ) : (
          <>
            <Database className="w-4 h-4" />
            Connect CRM Object
          </>
        )}
      </button>
    </div>
  );
}

export default function KnowledgePanel({ chatbotId }) {
  const { showError, showSuccess } = useToast();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const pollingRef = useRef(null);

  const fetchSources = useCallback(async () => {
    try {
      const response = await apiRequest(`/chatbots/${chatbotId}/knowledge-sources/`);
      if (response.ok) {
        const data = await response.json();
        setSources(data);
        return data;
      }
    } catch (error) {
      console.error('Failed to fetch sources:', error);
    } finally {
      setLoading(false);
    }
    return [];
  }, [chatbotId]);

  // Initial fetch
  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  // Poll for processing sources
  useEffect(() => {
    // Always clear any existing interval first to prevent stacking
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    const hasProcessing = sources.some((s) => s.status === 'processing' || s.status === 'pending');

    if (hasProcessing) {
      pollingRef.current = setInterval(() => {
        fetchSources();
      }, 3000);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [sources, fetchSources]);

  const handleDelete = async (sourceId) => {
    if (!window.confirm('Delete this knowledge source and all its chunks?')) return;
    setDeletingId(sourceId);

    try {
      const response = await apiRequest(
        `/chatbots/${chatbotId}/knowledge-sources/${sourceId}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        showSuccess('Knowledge source deleted');
        setSources((prev) => prev.filter((s) => s.id !== sourceId));
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      showError('Failed to delete knowledge source');
    } finally {
      setDeletingId(null);
    }
  };

  const handleRefresh = async (sourceId) => {
    setRefreshingId(sourceId);
    try {
      const response = await apiRequest(
        `/chatbots/${chatbotId}/knowledge-sources/${sourceId}/refresh`,
        { method: 'POST' }
      );
      if (response.ok) {
        showSuccess('Re-processing started');
        fetchSources();
      } else {
        throw new Error('Failed to refresh');
      }
    } catch (error) {
      showError('Failed to refresh knowledge source');
    } finally {
      setRefreshingId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return null;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Header with add button */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Add documents, URLs, or CRM data to give your chatbot knowledge.
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Source
          </button>
        </div>

        {/* Sources list */}
        {sources.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
            <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-3">
              <BookOpen className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No knowledge sources yet</p>
            <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">
              Upload documents, add URLs, or connect CRM data to train your chatbot.
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-brand-600 dark:text-brand-400 border border-brand-200 dark:border-brand-800 hover:bg-brand-50 dark:hover:bg-brand-900/20 rounded-lg transition-colors cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              Add Your First Source
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {sources.map((source) => {
              const isDeleting = deletingId === source.id;
              const isRefreshing = refreshingId === source.id;

              return (
                <div
                  key={source.id}
                  className={`group flex items-center gap-3 px-3.5 py-3 rounded-xl border transition-colors ${
                    source.status === 'failed'
                      ? 'border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-900/10'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <SourceTypeIcon type={source.type} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {source.name}
                      </p>
                      <StatusBadge status={source.status} />
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {source.chunk_count > 0 && (
                        <span className="text-xs text-gray-400">
                          {source.chunk_count} chunk{source.chunk_count !== 1 ? 's' : ''}
                        </span>
                      )}
                      {source.file_size && (
                        <span className="text-xs text-gray-400">
                          {formatFileSize(source.file_size)}
                        </span>
                      )}
                      {source.url && (
                        <span className="text-xs text-gray-400 truncate max-w-[200px]">
                          {source.url}
                        </span>
                      )}
                      {source.error_message && (
                        <span className="text-xs text-red-500 truncate max-w-[250px]" title={source.error_message}>
                          {source.error_message}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {source.status !== 'processing' && (
                      <button
                        onClick={() => handleRefresh(source.id)}
                        disabled={isRefreshing}
                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer"
                        title="Refresh"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(source.id)}
                      disabled={isDeleting}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                      title="Delete"
                    >
                      {isDeleting ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Source Modal */}
      {showAddModal && (
        <AddSourceModal
          chatbotId={chatbotId}
          onClose={() => setShowAddModal(false)}
          onSourceAdded={fetchSources}
        />
      )}
    </>
  );
}
